import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import * as inventoryService from '../services/inventoryService';

export const usePurchaseReturn = ({ isOpen, initialInvoiceNo, serviceType, dynamicSettings, onSuccess, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [form, setForm] = useState({
    invoiceNo: "",
    vendor: "Select Vendor",
    reason: "Defective / Damaged on Arrival",
    invoiceDate: "",
    buyer: "VERVE KITCHENS",
    store: "Select Store",
    returnDate: new Date().toISOString().split('T')[0],
    billNo: "",
    remarks: "",
    refundMode: "bank",
    toAccount: "",
    adjustment: 0,
    receivedAmount: "",
    transactionId: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    receiverName: "",
    receiptNo: "",
    shippingFrom: "",
    shippingTo: "",
    transportMode: "Road",
    vehicleNo: "",
    trackingNo: "",
    shippingDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadPurchases();
      if (initialInvoiceNo) {
        handleFetchInvoice(initialInvoiceNo);
      }
    }
  }, [isOpen, serviceType]);

  const resetForm = () => {
    setForm({
      invoiceNo: "",
      vendor: "Select Vendor",
      reason: "Defective / Damaged on Arrival",
      invoiceDate: "",
      buyer: "VERVE KITCHENS",
      store: "Select Store",
      returnDate: new Date().toISOString().split('T')[0],
      billNo: `PR-${Date.now().toString().slice(-6)}`,
      remarks: "",
      refundMode: "bank",
      toAccount: "",
      adjustment: 0,
      receivedAmount: "",
      transactionId: "",
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      receiverName: "",
      receiptNo: "",
      shippingFrom: "",
      shippingTo: "",
      transportMode: "Road",
      vehicleNo: "",
      trackingNo: "",
      shippingDate: new Date().toISOString().split('T')[0]
    });
    setRows([]);
    setSelectedPurchase(null);
    setSearchText("");
    setShowDropdown(false);
  };

  const loadPurchases = async () => {
    try {
      const res = await inventoryService.fetchPurchases(serviceType);
      // Standardize the response data (handle paginated or simple list)
      const pArray = res.data?.data || res.data || (Array.isArray(res) ? res : []);
      const normalPurchases = (Array.isArray(pArray) ? pArray : []).filter(p => {
        return !(p.isOpenStock || (p.invoiceNo && p.invoiceNo.startsWith('OPEN-STOCK')) || (typeof p.vendor === 'string' && p.vendor === 'Opening Stock'));
      });
      setPurchases(normalPurchases);
    } catch (e) {
      console.error("Fetch purchases error:", e);
    }
  };

  const filteredInvoices = useMemo(() => {
    if (!Array.isArray(purchases)) return [];

    // Deduplicate by Invoice Number to avoid showing the same invoice multiple times
    const uniqueInvoices = [];
    const seenInvoices = new Set();

    purchases.forEach(p => {
      const invNo = (p.invoiceNo || p.invoiceNumber || p.billNo || "").trim();
      if (invNo && !seenInvoices.has(invNo)) {
        seenInvoices.add(invNo);
        uniqueInvoices.push(p);
      }
    });

    if (!searchText) return uniqueInvoices.slice(0, 5);
    return uniqueInvoices.filter(p =>
      (p.invoiceNo || p.invoiceNumber || p.billNo || "").toLowerCase().includes(searchText.toLowerCase())
    ).slice(0, 10);
  }, [purchases, searchText]);

  const handleFetchInvoice = async (invNo) => {
    if (!invNo) return;
    setFetching(true);
    try {
      const res = await inventoryService.fetchPurchaseByInvoice(invNo);
      // Unwrap the nested data if present
      const invoiceData = res.data?.data || res.data || res;
      if (invoiceData && (invoiceData.invoiceNumber || invoiceData.invoiceNo || invoiceData.billNo)) {
        handleLoadPurchase(invoiceData);
        toast.success("Invoice loaded successfully");
      } else {
        toast.error("Invoice not found");
      }
    } catch (e) {
      toast.error(e.message || "Failed to fetch invoice");
    } finally {
      setFetching(false);
    }
  };

  const handleLoadPurchase = (p) => {
    if (!p) return;
    setSelectedPurchase(p);
    console.log("handleLoadPurchase", p);
    // 1. Resolve Vendor
    const vRef = p.vendor;
    const vId = typeof vRef === 'object' ? vRef?._id : vRef;
    const vSettings = dynamicSettings?.suppliers?.find(s => s._id === vId || s.name === vId || s.title === vId);
    const vendorName = (typeof vRef === 'object' ? (vRef?.vendorName || vRef?.name || vRef?.title) : null) ||
      (vSettings?.vendorName || vSettings?.name || vSettings?.title) ||
      (typeof vRef === 'string' ? vRef : "N/A");

    // 2. Resolve Store
    const sRef = p.store;
    const sId = typeof sRef === 'object' ? sRef?._id : sRef;
    const sSettings = dynamicSettings?.stores?.find(st => st._id === sId || st.storeName === sId || st.title === sId || st.name === sId);
    const storeName = (typeof sRef === 'object' ? (sRef?.storeName || sRef?.title || sRef?.name) : null) ||
      (sSettings?.storeName || sSettings?.title || sSettings?.name) ||
      (typeof sRef === 'string' ? sRef : "N/A");

    const vendorAddress = (typeof vRef === 'object' ? (vRef?.addressLine1 || vRef?.address || vRef?.location) : null) || vSettings?.addressLine1 || vSettings?.address || "N/A";
    const storeAddress = (typeof sRef === 'object' ? (sRef?.addressLine1 || sRef?.address || sRef?.location) : null) || sSettings?.addressLine1 || sSettings?.address || "N/A";

    setForm(prev => ({
      ...prev,
      invoiceNo: p.invoiceNo || p.invoiceNumber || p.billNo || "",
      vendor: vendorName,
      invoiceDate: p.date ? new Date(p.date).toISOString().split('T')[0] : (p.invoiceDate ? new Date(p.invoiceDate).toISOString().split('T')[0] : ""),
      store: storeName,
      shippingFrom: storeAddress,
      shippingTo: vendorAddress,
      reason: prev.reason || "Defective / Damaged on Arrival"
    }));
    setSearchText(p.invoiceNo || p.invoiceNumber || p.billNo || "");
    setShowDropdown(false);

    if (p.items && Array.isArray(p.items)) {
      const allItems = p.items.map(it => {
        const itemSetting = (it.item && typeof it.item === 'object') ? it.item : {};

        // Resolve Brand
        const bId = it.brand?._id || it.brand || itemSetting?.brand?._id || itemSetting?.brand;
        const bSettings = dynamicSettings?.brands?.find(b => b._id === bId || b.title === bId);
        const brand = (it.brand?.title || it.brand?.name) ||
          (itemSetting?.brand?.title || itemSetting?.brand?.name) ||
          (bSettings?.title || bSettings?.name) || "N/A";

        // Resolve Category
        const cId = it.category?._id || it.category || itemSetting?.category?._id || itemSetting?.category;
        const cSettings = dynamicSettings?.cats?.find(c => c._id === cId || c.title === cId);
        const category = (it.category?.title || it.category?.name) ||
          (itemSetting?.category?.title || itemSetting?.category?.name) ||
          (cSettings?.title || cSettings?.name) || "N/A";

        // Resolve Unit
        const uId = it.unit?._id || it.unit || itemSetting?.units?._id || itemSetting?.units || itemSetting?.unit;
        const uSettings = dynamicSettings?.units?.find(u => u._id === uId || u.title === uId);
        const unit = (it.unit?.title || it.unit?.name) ||
          (itemSetting?.units?.title || itemSetting?.units?.name) ||
          (uSettings?.title || uSettings?.name) ||
          (itemSetting?.unit) || "Unit";

        const itemCode = itemSetting?.itemCode || itemSetting?.sku || it.sku || "N/A";

        const q = Number(it.quantity || it.qty || 0);
        const r = Number(it.purchaseRate || it.rate || it.unitPrice || it.price || 0);
        const currentWhStock = Number(it.availableQty !== undefined ? it.availableQty : (it.currentStock || 0));
        const canReturn = Math.min(q, currentWhStock);

        return {
          _id: it._id,
          itemId: itemSetting?._id || it.item,
          name: itemSetting?.itemName || itemSetting?.name || itemSetting?.title || it.description || "Unknown Item",
          itemCode,
          brand,
          category,
          hsnCode: it.hsnCode || itemSetting.hsnCode || itemSetting.hsnSacCode || "N/A",
          unit,
          rate: r,
          maxReturn: canReturn,
          returnedQty: 0,
          selected: false,
          taxPercent: it.taxPercent || itemSetting.taxPercent || 0
        };
      });
      setRows(allItems);
    }
  };

  const toggleRowSelection = (index) => {
    const newRows = [...rows];
    const item = newRows[index];
    item.selected = !item.selected;
    // If selected, pre-fill with max return if currently 0
    if (item.selected && Number(item.returnedQty) === 0) {
      item.returnedQty = item.maxReturn;
    } else if (!item.selected) {
      item.returnedQty = 0;
    }
    setRows(newRows);
  };

  const updateRow = (index, qty) => {
    const newRows = [...rows];
    const max = newRows[index].maxReturn;
    if (qty > max) {
      toast.error(`Cannot return more than remaining quantity (${max})`);
      newRows[index].returnedQty = max;
    } else {
      newRows[index].returnedQty = qty;
    }
    setRows(newRows);
  };

  const totalRefund = useMemo(() => {
    const itemsTotal = rows.reduce((sum, row) => sum + (row.selected ? (row.returnedQty * (row.rate || 0)) : 0), 0);
    return itemsTotal + Number(form.adjustment || 0);
  }, [rows, form.adjustment]);

  const handleSubmit = async () => {
    const returnItems = rows.filter(r => r.selected && r.returnedQty > 0);
    if (returnItems.length === 0) {
      toast.error("Please specify items to return");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        returnType: 'PURCHASE',
        purchaseId: selectedPurchase?._id,
        returnNo: form.billNo,
        vendor: selectedPurchase?.vendor?._id || selectedPurchase?.vendor,
        store: selectedPurchase?.store?._id || selectedPurchase?.store,
        project: undefined,
        items: returnItems.map(r => ({
          purchaseItemId: r._id,
          inventoryId: r.itemId,
          itemName: r.name,
          quantity: r.returnedQty,
          rate: r.rate,
          reason: form.reason
        })),
        refundAmount: totalRefund,
        totalAmount: totalRefund,
        notes: form.reason,
        returnDate: form.returnDate,
        billNo: form.billNo,
        transportMode: form.transportMode,
        vehicleNumber: form.vehicleNo,
        trackingNumber: form.trackingNo,
        serviceType
      };

      const res = await inventoryService.createPurchaseReturn(payload);
      if (res) {
        toast.success("Return recorded successfully");
        onSuccess?.();
        onClose();
      }
    } catch (e) {
      toast.error(e.message || "Failed to record return");
    } finally {
      setLoading(false);
    }
  };

  return {
    form, setForm,
    rows, updateRow, toggleRowSelection,
    loading, fetching,
    searchText, setSearchText,
    showDropdown, setShowDropdown,
    filteredInvoices,
    handleFetchInvoice,
    handleLoadPurchase,
    totalRefund,
    handleSubmit,
    resetForm
  };
};
